// src/utils/weeklyChallengeStorage.js

const STORAGE_PREFIX = 'educat_weekly_challenge_';

const getStorageKey = (challengeId) => `${STORAGE_PREFIX}${challengeId}`;

/**
 * Obtiene el progreso guardado de un desafío semanal.
 * @param {string} challengeId
 * @returns {{ challengeId: string, sections: Record<string, { status: string, score?: number, total?: number, accuracy?: number, completedAt?: string }>, updatedAt?: string }}
 */
export const getWeeklyChallengeProgress = (challengeId) => {
  if (!challengeId) return { challengeId: '', sections: {} };
  try {
    const raw = localStorage.getItem(getStorageKey(challengeId));
    if (!raw) return { challengeId, sections: {} };
    const parsed = JSON.parse(raw);
    return {
      challengeId,
      sections: parsed.sections || {}
    };
  } catch (error) {
    console.error(`Error reading weekly challenge progress for ${challengeId}:`, error);
    return { challengeId, sections: {} };
  }
};

/**
 * Guarda o actualiza el progreso parcial de una sección (ej. in_progress).
 * @param {string} challengeId
 * @param {string} sectionId
 * @param {object} payload
 */
export const saveWeeklySectionProgress = (challengeId, sectionId, payload = {}) => {
  if (!challengeId || !sectionId) return;
  try {
    const current = getWeeklyChallengeProgress(challengeId);
    const existingSection = current.sections[sectionId] || {};

    current.sections[sectionId] = {
      ...existingSection,
      ...payload,
      updatedAt: new Date().toISOString()
    };
    current.updatedAt = new Date().toISOString();

    localStorage.setItem(getStorageKey(challengeId), JSON.stringify(current));
  } catch (error) {
    console.error(`Error saving section progress for ${sectionId}:`, error);
  }
};

/**
 * Marca una sección del desafío semanal como completada.
 * @param {string} challengeId
 * @param {string} sectionId
 * @param {{ score?: number, total?: number, accuracy?: number, completedAt?: string }} payload
 */
export const markWeeklySectionCompleted = (challengeId, sectionId, payload = {}) => {
  if (!challengeId || !sectionId) return;
  try {
    const current = getWeeklyChallengeProgress(challengeId);
    current.sections[sectionId] = {
      ...(current.sections[sectionId] || {}),
      status: 'completed',
      score: payload.score !== undefined ? payload.score : 0,
      total: payload.total !== undefined ? payload.total : 0,
      accuracy: payload.accuracy !== undefined ? payload.accuracy : 100,
      completedAt: payload.completedAt || new Date().toISOString()
    };
    current.updatedAt = new Date().toISOString();

    localStorage.setItem(getStorageKey(challengeId), JSON.stringify(current));
  } catch (error) {
    console.error(`Error marking section completed for ${sectionId}:`, error);
  }
};

/**
 * Determina si todas las secciones del desafío semanal están completadas.
 * @param {string} challengeId
 * @param {number|string[]} sectionsOrCount - Número total o arreglo de IDs de secciones
 * @returns {boolean}
 */
export const isWeeklyChallengeCompleted = (challengeId, sectionsOrCount) => {
  if (!challengeId) return false;
  try {
    const progress = getWeeklyChallengeProgress(challengeId);
    const completedCount = Object.values(progress.sections || {}).filter(
      (s) => s?.status === 'completed'
    ).length;

    if (Array.isArray(sectionsOrCount)) {
      if (sectionsOrCount.length === 0) return false;
      return sectionsOrCount.every((id) => progress.sections?.[id]?.status === 'completed');
    }

    const targetTotal = Number(sectionsOrCount) || 0;
    return targetTotal > 0 && completedCount >= targetTotal;
  } catch (error) {
    console.error(`Error verifying weekly challenge completion for ${challengeId}:`, error);
    return false;
  }
};

/**
 * Limpia el progreso guardado de un desafío semanal (útil para pruebas o reiniciar).
 * @param {string} challengeId
 */
export const clearWeeklyChallengeProgress = (challengeId) => {
  if (!challengeId) return;
  try {
    localStorage.removeItem(getStorageKey(challengeId));
  } catch (error) {
    console.error(`Error clearing weekly challenge progress for ${challengeId}:`, error);
  }
};
